import { useMemo, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { useGetEventTubularsQuery } from "@api/pApi";
import { useGetResolvedCatalogQuery } from "@api/mApi";
import { Modal } from "@components/ui/Modal";
import { PicklistSelect } from "@components/ui/PicklistSelect";
import { fmtDepth } from "@lib/format";
import type { AssemblyOut, CasingOut, PipeTallyOut } from "@api/types";
import type { Locale } from "@i18n/locales";
import {
  useCreateAssemblyMutation,
  useUpdateAssemblyMutation,
  useCreateCasingMutation,
  useUpdateCasingMutation,
  useCreatePipeTallyMutation,
  useUpdatePipeTallyMutation,
  type AssemblyCreate,
  type CasingCreate,
  type PipeTallyCreate,
} from "./tubularsApi";
import { rowKey, type AssemblyRow, type CasingRow, type PipeTallyRow } from "./types";
import "./tubulars.css";

// ── small helpers ───────────────────────────────────────────────────────────

/** Parse a text field into a nullable float (blank → null). */
function num(v: string): number | null {
  const s = v.trim();
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}
/** Parse into a nullable int. */
function int(v: string): number | null {
  const n = num(v);
  return n == null ? null : Math.trunc(n);
}
/** Trim to null. */
function str(v: string): string | null {
  const s = v.trim();
  return s === "" ? null : s;
}
/** Number/int → string for a controlled input. */
function s(v: number | null | undefined): string {
  return v == null ? "" : String(v);
}

interface CatalogOption {
  entry_key: string;
  display_label: string;
}
function useCatalogOptions(categoryKey: string): CatalogOption[] {
  const { data } = useGetResolvedCatalogQuery({ categoryKey });
  return data?.entries ?? [];
}

/** Yes / No / — tri-state select over a nullable boolean. */
function BoolSelect({
  value,
  onChange,
  yes,
  no,
  placeholder,
}: {
  value: boolean | null;
  onChange: (v: boolean | null) => void;
  yes: string;
  no: string;
  placeholder: string;
}) {
  const v = value == null ? "" : value ? "1" : "0";
  return (
    <select
      value={v}
      onChange={(e) =>
        onChange(e.target.value === "" ? null : e.target.value === "1")
      }
    >
      <option value="">{placeholder}</option>
      <option value="1">{yes}</option>
      <option value="0">{no}</option>
    </select>
  );
}

/** A short, mutation-scoped error line. 403 → admins/operators only. */
function MutError({ error }: { error: unknown }) {
  const { t } = useLocale();
  if (!error) return null;
  const status = (error as { status?: number }).status;
  const msg = status === 403 ? t("tub.edit.forbidden") : t("tub.edit.error");
  return (
    <div className="empty-state" style={{ color: "var(--crit, #c0392b)", padding: "6px 0" }}>
      {msg}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Assemblies
// ════════════════════════════════════════════════════════════════════════════

type AssemblyForm = Omit<AssemblyCreate, "well_uwi">;

function emptyAssembly(): AssemblyForm {
  return {
    assembly_name: null,
    assembly_no: null,
    string_type_entry_key: null,
    phase_entry_key: null,
    is_casing_liner: null,
    md_top: null,
    md_base: null,
    hole_size: null,
    length_total: null,
  };
}
function assemblyToForm(a: AssemblyOut): AssemblyForm {
  return {
    assembly_name: a.assembly_name,
    assembly_no: a.assembly_no,
    string_type_entry_key: a.string_type_entry_key,
    phase_entry_key: a.phase_entry_key,
    is_casing_liner: a.is_casing_liner,
    md_top: a.md_top,
    md_base: a.md_base,
    hole_size: a.hole_size,
    length_total: a.length_total,
  };
}

function AssemblyFields({
  form,
  set,
}: {
  form: AssemblyForm;
  set: (patch: Partial<AssemblyForm>) => void;
}) {
  const { t } = useLocale();
  return (
    <div className="edit-grid">
      <label>
        {t("tub.col.no")}
        <input
          type="number"
          value={s(form.assembly_no)}
          onChange={(e) => set({ assembly_no: int(e.target.value) })}
        />
      </label>
      <label>
        {t("tub.col.name")}
        <input
          value={form.assembly_name ?? ""}
          onChange={(e) => set({ assembly_name: str(e.target.value) })}
        />
      </label>
      <label>
        {t("tub.col.type")}
        <PicklistSelect
          categoryKey="string_types"
          value={form.string_type_entry_key ?? ""}
          onChange={(v) => set({ string_type_entry_key: v === "" ? null : v })}
          placeholder={t("tub.edit.selectType")}
        />
      </label>
      <label>
        {t("tub.col.phase")}
        <PicklistSelect
          categoryKey="activity_phases"
          value={form.phase_entry_key ?? ""}
          onChange={(v) => set({ phase_entry_key: v === "" ? null : v })}
          placeholder={t("tub.edit.selectPhase")}
        />
      </label>
      <label>
        {t("tub.col.casingLiner")}
        <BoolSelect
          value={form.is_casing_liner ?? null}
          onChange={(v) => set({ is_casing_liner: v })}
          yes={t("tub.yes")}
          no={t("tub.no")}
          placeholder={t("common.na")}
        />
      </label>
      <label>
        {t("tub.col.mdTop")}
        <input
          type="number"
          value={s(form.md_top)}
          onChange={(e) => set({ md_top: num(e.target.value) })}
        />
      </label>
      <label>
        {t("tub.col.mdBase")}
        <input
          type="number"
          value={s(form.md_base)}
          onChange={(e) => set({ md_base: num(e.target.value) })}
        />
      </label>
      <label>
        {t("tub.col.holeSize")}
        <input
          type="number"
          value={s(form.hole_size)}
          onChange={(e) => set({ hole_size: num(e.target.value) })}
        />
      </label>
      <label>
        {t("tub.col.length")}
        <input
          type="number"
          value={s(form.length_total)}
          onChange={(e) => set({ length_total: num(e.target.value) })}
        />
      </label>
    </div>
  );
}

function AssembliesSection({
  eventId,
  wellUwi,
  rows,
}: {
  eventId: string;
  wellUwi: string;
  rows: AssemblyRow[];
}) {
  const { t, locale } = useLocale();
  const stringTypes = useCatalogOptions("string_types");
  const phases = useCatalogOptions("activity_phases");
  const [create, createState] = useCreateAssemblyMutation();
  const [update, updateState] = useUpdateAssemblyMutation();
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState<AssemblyForm>(emptyAssembly);
  const [editRow, setEditRow] = useState<AssemblyRow | null>(null);
  const [editForm, setEditForm] = useState<AssemblyForm>(emptyAssembly);

  async function submitAdd() {
    try {
      await create({ eventId, body: { well_uwi: wellUwi, ...addForm } }).unwrap();
      setAdding(false);
      setAddForm(emptyAssembly());
    } catch {
      /* error surfaced via createState.error (MutError) */
    }
  }
  async function submitEdit(row: AssemblyRow) {
    try {
      await update({ eventId, entityId: rowKey(row), body: editForm }).unwrap();
      setEditRow(null);
    } catch {
      /* error surfaced via updateState.error (MutError) */
    }
  }

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="section-head">
        <h3>{t("tub.assemblies")}</h3>
        <button
          type="button"
          className="btn"
          onClick={() => {
            setAddForm(emptyAssembly());
            setAdding(true);
          }}
        >
          {t("tub.edit.add")}
        </button>
      </div>

      <Modal
        open={adding}
        title={t("tub.edit.add")}
        onClose={() => setAdding(false)}
        onConfirm={submitAdd}
        busy={createState.isLoading}
        confirmLabel={createState.isLoading ? t("tub.edit.saving") : t("tub.edit.save")}
        cancelLabel={t("tub.edit.cancel")}
        footerNote={<MutError error={createState.error} />}
      >
        <AssemblyFields form={addForm} set={(p) => setAddForm((f) => ({ ...f, ...p }))} />
      </Modal>

      <Modal
        open={editRow !== null}
        title={t("tub.edit.edit")}
        onClose={() => setEditRow(null)}
        onConfirm={() => editRow && submitEdit(editRow)}
        busy={updateState.isLoading}
        confirmLabel={updateState.isLoading ? t("tub.edit.saving") : t("tub.edit.save")}
        cancelLabel={t("tub.edit.cancel")}
        footerNote={<MutError error={updateState.error} />}
      >
        <AssemblyFields form={editForm} set={(p) => setEditForm((f) => ({ ...f, ...p }))} />
      </Modal>

      {rows.length === 0 ? (
        <div className="empty-state">{t("tub.assemblies.empty")}</div>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th className="num">{t("tub.col.no")}</th>
                <th>{t("tub.col.name")}</th>
                <th>{t("tub.col.type")}</th>
                <th>{t("tub.col.phase")}</th>
                <th className="num">{t("tub.col.mdTop")}</th>
                <th className="num">{t("tub.col.mdBase")}</th>
                <th className="num">{t("tub.col.length")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} data-owner="p">
                  <td className="num tab-nums">{a.assembly_no ?? "—"}</td>
                  <td>{a.assembly_name ?? "—"}</td>
                  <td>
                    <span className="mono">{labelFor(stringTypes, a.string_type_entry_key)}</span>
                  </td>
                  <td>{labelFor(phases, a.phase_entry_key)}</td>
                  <td className="num tab-nums">{fmtDepth(a.md_top, locale)}</td>
                  <td className="num tab-nums">{fmtDepth(a.md_base, locale)}</td>
                  <td className="num tab-nums">{fmtDepth(a.length_total, locale)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => {
                        setEditForm(assemblyToForm(a));
                        setEditRow(a);
                      }}
                    >
                      {t("tub.edit.edit")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Casing
// ════════════════════════════════════════════════════════════════════════════

type CasingForm = Omit<CasingCreate, "well_uwi">;

function emptyCasing(): CasingForm {
  return { assembly_id: null, report_no: null, date_report: null };
}
function casingToForm(c: CasingOut): CasingForm {
  return { assembly_id: c.assembly_id, report_no: c.report_no, date_report: c.date_report };
}

function CasingFields({ form, set }: { form: CasingForm; set: (p: Partial<CasingForm>) => void }) {
  const { t } = useLocale();
  return (
    <div className="edit-grid">
      <label>
        {t("tub.col.report")}
        <input type="number" value={s(form.report_no)} onChange={(e) => set({ report_no: int(e.target.value) })} />
      </label>
      <label>
        {t("tub.col.assembly")}
        <input value={form.assembly_id ?? ""} onChange={(e) => set({ assembly_id: str(e.target.value) })} />
      </label>
      <label>
        {t("tub.col.date")}
        <input type="date" value={form.date_report ?? ""} onChange={(e) => set({ date_report: str(e.target.value) })} />
      </label>
    </div>
  );
}

function CasingSection({
  eventId,
  wellUwi,
  rows,
}: {
  eventId: string;
  wellUwi: string;
  rows: CasingRow[];
}) {
  const { t, locale } = useLocale();
  const [create, createState] = useCreateCasingMutation();
  const [update, updateState] = useUpdateCasingMutation();
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState<CasingForm>(emptyCasing);
  const [editRow, setEditRow] = useState<CasingRow | null>(null);
  const [editForm, setEditForm] = useState<CasingForm>(emptyCasing);

  async function submitAdd() {
    try {
      await create({ eventId, body: { well_uwi: wellUwi, ...addForm } }).unwrap();
      setAdding(false);
      setAddForm(emptyCasing());
    } catch {
      /* error surfaced via createState.error (MutError) */
    }
  }
  async function submitEdit(row: CasingRow) {
    try {
      await update({ eventId, entityId: rowKey(row), body: editForm }).unwrap();
      setEditRow(null);
    } catch {
      /* error surfaced via updateState.error (MutError) */
    }
  }

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="section-head">
        <h3>{t("tub.casing")}</h3>
        <button type="button" className="btn" onClick={() => { setAddForm(emptyCasing()); setAdding(true); }}>
          {t("tub.edit.add")}
        </button>
      </div>

      <Modal
        open={adding}
        title={t("tub.edit.add")}
        onClose={() => setAdding(false)}
        onConfirm={submitAdd}
        busy={createState.isLoading}
        confirmLabel={createState.isLoading ? t("tub.edit.saving") : t("tub.edit.save")}
        cancelLabel={t("tub.edit.cancel")}
        footerNote={<MutError error={createState.error} />}
      >
        <CasingFields form={addForm} set={(p) => setAddForm((f) => ({ ...f, ...p }))} />
      </Modal>

      <Modal
        open={editRow !== null}
        title={t("tub.edit.edit")}
        onClose={() => setEditRow(null)}
        onConfirm={() => editRow && submitEdit(editRow)}
        busy={updateState.isLoading}
        confirmLabel={updateState.isLoading ? t("tub.edit.saving") : t("tub.edit.save")}
        cancelLabel={t("tub.edit.cancel")}
        footerNote={<MutError error={updateState.error} />}
      >
        <CasingFields form={editForm} set={(p) => setEditForm((f) => ({ ...f, ...p }))} />
      </Modal>

      {rows.length === 0 ? (
        <div className="empty-state">{t("tub.casing.empty")}</div>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th className="num">{t("tub.col.report")}</th>
                <th>{t("tub.col.assembly")}</th>
                <th>{t("tub.col.date")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} data-owner="p">
                  <td className="num tab-nums">{c.report_no ?? "—"}</td>
                  <td><span className="mono">{c.assembly_id ?? "—"}</span></td>
                  <td>{fmtDate(c.date_report, locale)}</td>
                  <td>
                    <button type="button" className="btn" onClick={() => { setEditForm(casingToForm(c)); setEditRow(c); }}>
                      {t("tub.edit.edit")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Pipe tally
// ════════════════════════════════════════════════════════════════════════════

type PipeTallyForm = Omit<PipeTallyCreate, "well_uwi">;

function emptyPipeTally(): PipeTallyForm {
  return {
    pipe_run_ref: null,
    run_number: null,
    joint_number: null,
    sequence_no: null,
    length: null,
    cum_length: null,
    is_out: null,
  };
}
function pipeTallyToForm(p: PipeTallyOut): PipeTallyForm {
  return {
    pipe_run_ref: p.pipe_run_ref,
    run_number: p.run_number,
    joint_number: p.joint_number,
    sequence_no: p.sequence_no,
    length: p.length,
    cum_length: p.cum_length,
    is_out: p.is_out,
  };
}

function PipeTallyFields({ form, set }: { form: PipeTallyForm; set: (p: Partial<PipeTallyForm>) => void }) {
  const { t } = useLocale();
  return (
    <div className="edit-grid">
      <label>
        {t("tub.col.run")}
        <input type="number" value={s(form.run_number)} onChange={(e) => set({ run_number: int(e.target.value) })} />
      </label>
      <label>
        {t("tub.col.joint")}
        <input type="number" value={s(form.joint_number)} onChange={(e) => set({ joint_number: int(e.target.value) })} />
      </label>
      <label>
        {t("tub.col.seq")}
        <input type="number" value={s(form.sequence_no)} onChange={(e) => set({ sequence_no: int(e.target.value) })} />
      </label>
      <label>
        {t("tub.col.length")}
        <input type="number" value={s(form.length)} onChange={(e) => set({ length: num(e.target.value) })} />
      </label>
      <label>
        {t("tub.col.cumLength")}
        <input type="number" value={s(form.cum_length)} onChange={(e) => set({ cum_length: num(e.target.value) })} />
      </label>
      <label>
        {t("tub.col.status")}
        <BoolSelect
          value={form.is_out ?? null}
          onChange={(v) => set({ is_out: v })}
          yes={t("tub.status.out")}
          no={t("tub.status.in")}
          placeholder={t("common.na")}
        />
      </label>
    </div>
  );
}

function PipeTallySection({
  eventId,
  wellUwi,
  rows,
}: {
  eventId: string;
  wellUwi: string;
  rows: PipeTallyRow[];
}) {
  const { t, locale } = useLocale();
  const [create, createState] = useCreatePipeTallyMutation();
  const [update, updateState] = useUpdatePipeTallyMutation();
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState<PipeTallyForm>(emptyPipeTally);
  const [editRow, setEditRow] = useState<PipeTallyRow | null>(null);
  const [editForm, setEditForm] = useState<PipeTallyForm>(emptyPipeTally);

  async function submitAdd() {
    try {
      await create({ eventId, body: { well_uwi: wellUwi, ...addForm } }).unwrap();
      setAdding(false);
      setAddForm(emptyPipeTally());
    } catch {
      /* error surfaced via createState.error (MutError) */
    }
  }
  async function submitEdit(row: PipeTallyRow) {
    try {
      await update({ eventId, entityId: rowKey(row), body: editForm }).unwrap();
      setEditRow(null);
    } catch {
      /* error surfaced via updateState.error (MutError) */
    }
  }

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="section-head">
        <h3>{t("tub.pipeTally")}</h3>
        <button type="button" className="btn" onClick={() => { setAddForm(emptyPipeTally()); setAdding(true); }}>
          {t("tub.edit.add")}
        </button>
      </div>

      <Modal
        open={adding}
        title={t("tub.edit.add")}
        onClose={() => setAdding(false)}
        onConfirm={submitAdd}
        busy={createState.isLoading}
        confirmLabel={createState.isLoading ? t("tub.edit.saving") : t("tub.edit.save")}
        cancelLabel={t("tub.edit.cancel")}
        footerNote={<MutError error={createState.error} />}
      >
        <PipeTallyFields form={addForm} set={(p) => setAddForm((f) => ({ ...f, ...p }))} />
      </Modal>

      <Modal
        open={editRow !== null}
        title={t("tub.edit.edit")}
        onClose={() => setEditRow(null)}
        onConfirm={() => editRow && submitEdit(editRow)}
        busy={updateState.isLoading}
        confirmLabel={updateState.isLoading ? t("tub.edit.saving") : t("tub.edit.save")}
        cancelLabel={t("tub.edit.cancel")}
        footerNote={<MutError error={updateState.error} />}
      >
        <PipeTallyFields form={editForm} set={(p) => setEditForm((f) => ({ ...f, ...p }))} />
      </Modal>

      {rows.length === 0 ? (
        <div className="empty-state">{t("tub.pipeTally.empty")}</div>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th className="num">{t("tub.col.run")}</th>
                <th className="num">{t("tub.col.joint")}</th>
                <th className="num">{t("tub.col.seq")}</th>
                <th className="num">{t("tub.col.length")}</th>
                <th className="num">{t("tub.col.cumLength")}</th>
                <th>{t("tub.col.status")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} data-owner="p">
                  <td className="num tab-nums">{p.run_number ?? p.pipe_run_ref ?? "—"}</td>
                  <td className="num tab-nums">{p.joint_number ?? "—"}</td>
                  <td className="num tab-nums">{p.sequence_no ?? "—"}</td>
                  <td className="num tab-nums">{fmtDepth(p.length, locale)}</td>
                  <td className="num tab-nums">{fmtDepth(p.cum_length, locale)}</td>
                  <td>
                    {p.is_out == null ? (
                      "—"
                    ) : (
                      <span className={`badge ${p.is_out ? "status-completion" : "status-drilling"}`}>
                        {p.is_out ? t("tub.status.out") : t("tub.status.in")}
                      </span>
                    )}
                  </td>
                  <td>
                    <button type="button" className="btn" onClick={() => { setEditForm(pipeTallyToForm(p)); setEditRow(p); }}>
                      {t("tub.edit.edit")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Panel
// ════════════════════════════════════════════════════════════════════════════

/** entry_key → display_label, falling back to the raw key. */
function labelFor(options: CatalogOption[], key: string | null): string {
  if (!key) return "—";
  return options.find((o) => o.entry_key === key)?.display_label ?? key;
}

/** A yyyy-mm-dd report date in the tenant's locale. */
function fmtDate(iso: string | null, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
}

/**
 * Editable Tubulars panel — the event's assemblies / casing / pipe-tally rows,
 * each section with an inline Edit (PUT keyed on entity_id) and "+ Agregar"
 * (POST) form against `/events/{eventId}/tubulars/{…}`. `wellUwi` is required on
 * every POST. The tubulars list refetches on success (tag invalidation). A 403
 * surfaces "solo administradores / operadores".
 */
export function TubularsEditPanel({ eventId, wellUwi }: { eventId: string; wellUwi: string }) {
  const { t } = useLocale();
  const { data, isLoading, isError, refetch } = useGetEventTubularsQuery(eventId, {
    skip: !eventId,
  });

  const assemblies = useMemo<AssemblyRow[]>(
    () => (Array.isArray(data?.assemblies) ? (data!.assemblies as AssemblyRow[]) : []),
    [data],
  );
  const casing = useMemo<CasingRow[]>(
    () => (Array.isArray(data?.casing) ? (data!.casing as CasingRow[]) : []),
    [data],
  );
  const pipeTally = useMemo<PipeTallyRow[]>(
    () => (Array.isArray(data?.pipe_tally) ? (data!.pipe_tally as PipeTallyRow[]) : []),
    [data],
  );

  return (
    <QueryState isLoading={isLoading} isError={isError} onRetry={refetch}>
      <div className="tub-edit">
        <div className="page-head" style={{ marginBottom: 12 }}>
          <p className="subtitle">{t("tub.edit.subtitle")}</p>
        </div>
        <AssembliesSection eventId={eventId} wellUwi={wellUwi} rows={assemblies} />
        <CasingSection eventId={eventId} wellUwi={wellUwi} rows={casing} />
        <PipeTallySection eventId={eventId} wellUwi={wellUwi} rows={pipeTally} />
      </div>
    </QueryState>
  );
}
